import React, {Fragment, useEffect, useState} from 'react';
import classNames from 'classnames';
import {LoadingSpinner} from 'components';
import {selectUserProfile} from 'features/User';
import {Receipt, SliceStatus} from 'interfaces';
import {Trans, useTranslation} from 'react-i18next';
import {Document, Page, pdfjs} from 'react-pdf';
import {useDispatch, useSelector} from 'react-redux';
import {downloadFile} from 'utils';

import {Dialog, Transition} from '@headlessui/react';

import {fetchReceiptPdf} from './receiptActions';

import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export interface ReceiptPdfProps {
  receipt: Receipt;
  download: boolean;
  onClose: VoidFunction;
}

export function ReceiptPdf({
  receipt: item,
  ...props
}: ReceiptPdfProps): React.ReactElement {
  const [status, setStatus] = useState<SliceStatus>(SliceStatus.idle);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdf, setPdf] = useState<string | null>(null);
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectUserProfile);

  const fetchPdf = () => {
    setStatus(SliceStatus.pending);
    const onSuccess = async (data: Blob) => {
      const pdfBlob = new Blob([data], {type: 'application/pdf'});
      const url = URL.createObjectURL(pdfBlob);

      if (props.download) {
        await downloadFile(url, `${item.id}.pdf`);
        props.onClose();
      } else {
        setPdf(url);
      }
      setStatus(SliceStatus.resolved);
    };

    const onError = () => {
      setStatus(SliceStatus.rejected);
    };
    if (user) {
      dispatch(
        fetchReceiptPdf({
          id: item.id,
          role: user.role,
          onSuccess,
          onError,
        }),
      );
    }
  };

  useEffect(() => {
    fetchPdf();

    return () => {
      if (pdf) {
        URL.revokeObjectURL(pdf);
        setPdf(null);
      }
    };
  }, []);

  function onDocumentLoadSuccess({numPages}: {numPages: number}): void {
    setNumPages(numPages);
  }

  const onClose = () => {
    setPdf(null);
    setStatus(SliceStatus.idle);
    props.onClose();
  };

  return (
    <Transition
      appear
      show={!!pdf || status === SliceStatus.pending}
      as={Fragment}
    >
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  'w-fit transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all',
                  {
                    'rounded-2xl': status === SliceStatus.pending,
                  },
                )}
              >
                {!pdf || status === SliceStatus.pending ? (
                  <div className="p-5 flex flex-col justify-center items-center space-y-3">
                    <LoadingSpinner type="Oval" displayDelay={0} />
                    {props.download ? (
                      <Trans i18nKey="receipts.downloadingReceiptPdf" t={t}>
                        <p>Downloading Receipt...</p>
                      </Trans>
                    ) : (
                      <Trans i18nKey="receipts.loadingReceiptPdf" t={t}>
                        <p>Loading Receipt...</p>
                      </Trans>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Document
                      file={pdf}
                      onLoadSuccess={onDocumentLoadSuccess}
                      options={{
                        cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
                        cMapPacked: true,
                      }}
                      // prevent downloading the pdf
                      onContextMenu={e => e.preventDefault()}
                    >
                      {new Array(numPages).fill(null).map((_, page) => (
                        <Page
                          key={`receipt-pdf-page-${page}`}
                          pageNumber={page + 1}
                        />
                      ))}
                    </Document>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
